/**
 * Точка доступа к контенту сайта.
 *
 * Все тексты — в JSON-файлах этого каталога. Админка (CMS) правит JSON,
 * приложение читает его здесь через типизированные экспорты.
 * Ни один компонент не содержит текстов сайта напрямую.
 *
 * Карта файлов:
 *   site.json          — бренд, контакты, соцсети, футер, подписи форм
 *   navigation.json    — пункты меню для каждой страницы
 *   home.json          — главная
 *   analytics.json     — модуль аналитики
 *   integrations.json  — каталог интеграций и детальные страницы
 *   solutions.json     — готовые решения
 *   lean.json          — Lean-консалтинг
 *   booker.json        — БроньСРМ
 */

import siteJson from "./site.json";
import navigationJson from "./navigation.json";
import analyticsJson from "./analytics.json";
import integrationsJson from "./integrations.json";
import solutionsJson from "./solutions.json";
import homeJson from "./home.json";
import leanJson from "./lean.json";
import bookerJson from "./booker.json";

import type {
  SiteContent,
  NavigationContent,
  AnalyticsContent,
  IntegrationsContent,
  SolutionsContent,
  HomeContent,
  LeanContent,
  BookerContent,
  Integration,
  IntegrationCategory,
  Solution,
} from "./types";

export const site = siteJson as SiteContent;
export const navigation = navigationJson as NavigationContent;
export const analytics = analyticsJson as AnalyticsContent;
export const integrationsContent = integrationsJson as IntegrationsContent;
export const solutionsContent = solutionsJson as SolutionsContent;
export const home = homeJson as HomeContent;
export const lean = leanJson as LeanContent;
export const booker = bookerJson as BookerContent;

/* ───────────── Удобные выборки ───────────── */

export const integrations: Integration[] = integrationsContent.items;
export const integrationCategories: IntegrationCategory[] =
  integrationsContent.categories;
export const solutions: Solution[] = solutionsContent.items;

export const getIntegrationBySlug = (slug: string): Integration | undefined =>
  integrations.find((item) => item.slug === slug);

export const getCategoryBySlug = (
  slug: string
): IntegrationCategory | undefined =>
  integrationCategories.find((category) => category.slug === slug);

export const getIntegrationsByCategory = (
  categorySlug: string
): Integration[] =>
  integrations.filter((item) => item.category === categorySlug);

export const getRelatedIntegrations = (
  integration: Integration
): Integration[] =>
  integration.related
    .map((slug) => getIntegrationBySlug(slug))
    .filter((item): item is Integration => Boolean(item));

export const getIntegrationNeighbors = (
  integration: Integration
): { prev?: Integration; next?: Integration } => {
  const list = getIntegrationsByCategory(integration.category);
  const index = list.findIndex((item) => item.slug === integration.slug);
  return { prev: list[index - 1], next: list[index + 1] };
};

/** Склонение: 1 интеграция, 2 интеграции, 5 интеграций */
export function pluralize(count: number, forms: [string, string, string]): string {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}
